import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { PapiDeviation, PapiGallection } from '@wix/da-papi-types';
import { getEnv } from '@wix/da-react-app/pkg/client/Environment/redux/selectors';
import { StreamType } from '@wix/da-shared-react/pkg/Stream';
import { useNormalizedDeviationStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import { getFolderById } from '../../../../../selectors/folders';
import { isCurrentUserProfileOwner } from '../../../../../selectors/users';
import {
  getGallectionSectionType,
  getSelectedSubfolder,
} from '../../../../../selectors/gallectionSection';
import { getCurrentPage } from '../../../../../selectors/pageInfo';
import {
  DEVIATIONS_PER_FETCH_COUNT,
  FOLDER_DEVIATIONS_STREAM_GROUP_ID,
  getFolderDeviationsStreamId,
} from '../../../../../streams/folderDeviations';
import {
  getDeviationIdSeedFromSearch,
  getGalleryOrderFromSearch,
  mapOrderToPAPI,
} from '../../../../../utils/gallection';
import { fetchNextFolderDeviations } from '../../../../../actions/gallectionSection';
import { GallectionType } from '../../../../../../types/gallection';
import { AppState } from '../../../../../../types/store';

export interface UseInfiniteFolderDeviationsReturn {
  currentPage: number;
  deviations: PapiDeviation[];
  folder?: PapiGallection;
  subfolder?: PapiGallection;
  gallectionType: GallectionType;
  hasMore: boolean;
  isFetching: boolean;
  isOwner: boolean;
  requestUrl: string;
  streamId: string;
}

export default function useInfiniteFolderDeviations(
  folderId: number
): UseInfiniteFolderDeviationsReturn {
  const { search } = useLocation();
  const gallectionType = useSelector(getGallectionSectionType);
  const selectedSubfolder = useSelector(getSelectedSubfolder);
  const requestUrl = useSelector(state => getEnv(state).requestUrl) ?? '';
  const currentPage = useSelector(getCurrentPage);
  const isOwner = useSelector(isCurrentUserProfileOwner);
  const folder = useSelector((state: AppState) => getFolderById(state, gallectionType, folderId));
  const subfolder =
    selectedSubfolder && selectedSubfolder.parentId === folderId ? selectedSubfolder : undefined;
  const currentFolderId = subfolder?.folderId ?? folderId;
  const streamId = getFolderDeviationsStreamId(gallectionType, currentFolderId);
  const requestOrder = useMemo(() => mapOrderToPAPI(getGalleryOrderFromSearch(search)), [search]);
  const initConfig = useMemo(
    () => ({
      autoFetch: true,
      fetchNextCallback: fetchNextFolderDeviations,
      groupId: FOLDER_DEVIATIONS_STREAM_GROUP_ID,
      hasMore: true,
      itemsPerFetch: DEVIATIONS_PER_FETCH_COUNT,
      streamType: StreamType.WITH_OFFSET,
      streamParams: {
        deviationIdSeed: getDeviationIdSeedFromSearch(search),
        effectiveOrder: requestOrder ?? 'personalized',
        folderId: currentFolderId,
        gallectionType,
      },
    }),
    [currentFolderId, gallectionType, requestOrder, search]
  );
  const stream = useNormalizedDeviationStream(streamId, 500, initConfig);

  return {
    currentPage,
    deviations: stream.items,
    folder,
    subfolder,
    gallectionType,
    hasMore: stream.hasMore,
    isFetching: stream.isFetching,
    isOwner,
    requestUrl,
    streamId,
  };
}
